import dayjs from "@lib/dayjs";
import { EnumUserTypes, JobType } from "@utils/constants";
import {
  confirmExclusiveRepresentation,
  jobGetTracker,
} from "@endpoints/job.service";
import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { isNullOrEmpty } from "@utils/helperFunction";
import { TrackerStatus } from "@utils/types";
import { useSelector } from "react-redux";

const trackerDummyData = [
  {
    dateTime: "2026-03-26T14:46:56.036Z",
    status: "Successful",
    content: (
      <span>
        You have been successful in your application for the position titled{" "}
        <span className="text-blue-400">Senior Node.js Developer</span>
      </span>
    ),
  },
  {
    dateTime: "2026-03-24T04:46:56.036Z",
    status: "Offer Accepted",
    content: (
      <span>
        An offer has been accepted by you from{" "}
        <span className="text-blue-400">ABC Recruitment</span> on behalf of the
        hiring company for the position titled{" "}
        <span className="text-blue-400">Senior Node.js Developer</span>
      </span>
    ),
  },
  {
    dateTime: "2026-03-26T14:46:56.036Z",
    status: "Interview",
    content: (
      <span>
        An interview has been scheduled for{" "}
        <span className="text-blue-400">4:00 pm Friday 18th March 2026</span>{" "}
        with the hiring company at 123 High Street London
      </span>
    ),
  },
  {
    dateTime: "2026-03-24T04:46:56.036Z",
    status: "Shortlisted",
    content: (
      <span>
        Your application for the position of{" "}
        <span className="text-blue-400">Senior Node.js Developer</span> has been
        shortlisted by the hiring company.
      </span>
    ),
  },
  {
    dateTime: "2026-03-26T14:46:56.036Z",
    status: "Application Viewed",
    content: (
      <span>
        The hiring company has viewed your application submitted for the
        position of{" "}
        <span className="text-blue-400">Senior Node.js Developer</span> by{" "}
        <span className="text-blue-400">ABC Recruitment</span>
      </span>
    ),
  },
  {
    dateTime: "2026-03-24T04:46:56.036Z",
    status: "Submitted",
    content: (
      <span>
        Your application for the position of{" "}
        <span className="text-blue-400">Senior Node.js Developer</span> has been
        submitted by <span className="text-blue-500">ABC Recruitment</span> to
        the hiring company
      </span>
    ),
  },
  {
    dateTime: "2026-03-24T04:46:56.036Z",
    status: "Meeting",
    content: (
      <span>
        A phone call meeting has been scheduled at{" "}
        <span className="text-blue-400">4:00pm Friday 18th March 2026</span>{" "}
        with <span className="text-blue-500">ABC Recruitment</span>
      </span>
    ),
  },
  {
    dateTime: "2026-03-24T04:46:56.036Z",
    status: "Applied",
    content: (
      <span>
        You applied for the position titled{" "}
        <span className="text-blue-400">Senior Node.js Developer</span> posted
        by <span className="text-blue-500">ABC Recruitment</span>
      </span>
    ),
  },
];

const TrackerCard = ({ data }) => {
  return (
    <div className="grid grid-cols-[0.45fr_1.55fr] gap-8 items-center">
      <div className="flex flex-col gap-1.5">
        <span className="text-xss font-semibold text-primary">
          {dayjs(data?.dateTime).format("hh:mm a")}
        </span>
        <span className="text-xss text-light-black">
          {dayjs(data?.dateTime).format("ddd Do")}{" "}
          {dayjs(data?.dateTime).format("MMMM YYYY")}
        </span>
        <div className="flex border-l-3 font-semibold text-primary text-xss border-l-[3px] border-l-blue-400 border rounded-sm px-2 py-1">
          {data?.status}
        </div>
      </div>
      <div className=" flex flex-col justify-center text-xss font-medium text-primary border-b h-28 leading-6">
        {data?.content}
      </div>
    </div>
  );
};

const TrackerTab = ({ applicationDetails, jobDetails, refetchJobDetails }) => {
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector((state) => state?.auth?.user);
  const [trackerList, setTrackerList] = useState();
  const [loading, setLoading] = useState(false);
  const [
    isConfirmingExclusiveRepresentation,
    setIsConfirmingExclusiveRepresentation,
  ] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchTracker();
    }
  }, []);

  console.log("this is tracker time", dayjs().toISOString());

  const fetchTracker = async () => {
    try {
      setLoading(true);
      const { data, success } = await jobGetTracker(applicationDetails?._id);
      if (success && data) {
        const sortedData = data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setTrackerList(sortedData);
      }
    } catch (error) {
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  async function handleConfirmRequestExclusiveRepresentation() {
    try {
      setIsConfirmingExclusiveRepresentation(true);
      const { success } = await confirmExclusiveRepresentation({
        jobApplicationId: applicationDetails?._id,
      });
      if (success) {
        refetchJobDetails();
        enqueueSnackbar("Exclusive Representation confirmed successfully", {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
      console.error("Error: Confirming exclusive representation", error);
    } finally {
      setIsConfirmingExclusiveRepresentation(false);
    }
  }

  function fetchStatus() {
    if (!jobDetails?.isConfirmationRequiredForExclusiveRepresentation) {
      return "Confirm Exclusive Representation";
    } else if (jobDetails?.isConfirmationRequiredForExclusiveRepresentation) {
      return "Exclusive Representation Confirmed";
    }
  }

  const isConfirmationRequiredForExclusiveRepresentation =
    jobDetails?.isConfirmationRequiredForExclusiveRepresentation;

  // const showConfirmRequestExclusiveRepresentation =
  //   jobDetails?.isConfirmationRequiredForExclusiveRepresentation &&
  //   user?.userType === EnumUserTypes.CANDIDATE;

  console.log("this is job details", jobDetails);

  return (
    <div className="flex gap-6 h-screen overflow-hidden">
      <div className="w-3/5 overflow-y-auto h-full no-scrollbar">
        {loading ? (
          <div className="flex justify-center mt-40 h-full">
            <LoadingAnimation color="black" />
          </div>
        ) : isNullOrEmpty(trackerList) ? (
          <div className="flex justify-center mt-40 h-full font-medium">
            No data tracked!
          </div>
        ) : (
          <>
            {trackerDummyData?.map((item) => {
              return <TrackerCard data={item} />;
            })}
            {trackerList?.map((list, index) => {
              return (
                <div className="bg-[#FAFAFA] rounded-lg p-4 mb-3 border border-[#DDDDDD] flex gap-12 cursor-pointer hover:bg-gray-100">
                  <div className="flex flex-col gap-3.5 w-1/2">
                    <div className="flex flex-shrink-0 font-medium text-xs text-light-black gap-4">
                      {dayjs(list?.createdAt).format("h:mm a DD MMMM YYYY")}
                    </div>
                    {list?.status ===
                      TrackerStatus.REQUEST_EXCLUSIVE_REPRESENTATION &&
                    user?.userType === EnumUserTypes.CANDIDATE &&
                    !jobDetails?.isConfirmationRequiredForExclusiveRepresentation ? (
                      <button
                        onClick={handleConfirmRequestExclusiveRepresentation}
                        disabled={isConfirmingExclusiveRepresentation}
                        className={`flex items-center px-2 py-1.5 rounded text-center text-xs font-medium ${
                          isConfirmationRequiredForExclusiveRepresentation
                            ? "bg-[#F1F5F9] border border-[#DDDDDD] text-primary"
                            : "bg-green-600 text-white hover:bg-green-700"
                        }`}
                      >
                        {fetchStatus()}
                        {isConfirmingExclusiveRepresentation ? (
                          <LoadingAnimation />
                        ) : null}
                      </button>
                    ) : (
                      <div
                        className={`py-1.5 rounded text-center text-xs font-medium ${
                          list.title === "Offer Accepted"
                            ? "bg-light-black text-white"
                            : "bg-[#F1F5F9] border border-[#DDDDDD] text-primary"
                        }`}
                      >
                        {list?.status === "Submit Offer"
                          ? "Offer received"
                          : list?.status === "Decline"
                            ? "Application declined"
                            : list?.status === "Shortlist"
                              ? "Shortlisted"
                              : list?.status}
                      </div>
                    )}
                  </div>
                  <p className="bg-white text-light-black font-medium text-xs py-1.5 px-8 self-end rounded w-1/2 text-center h-7">
                    {dayjs(list?.createdAt).format("DD/MM/YY")}
                  </p>
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="bg-[#F1F5F9] p-4 space-y-3 rounded-lg w-2/5 h-3/5 overflow-y-hidden">
        <div className="flex justify-between">
          {applicationDetails?.job?.user?.profilePic ? (
            <img
              src={applicationDetails?.job?.user?.profilePic}
              className="rounded-full w-14 h-14 object-fill z-10 max-md:h-36 max-md:w-36"
            />
          ) : (
            <div className="font-montserrat text-white font-semibold uppercase w-14 h-14 rounded-full bg-gray-400 flex items-center justify-center text-xl">
              {applicationDetails?.job?.user?.firstName?.charAt(0)}
              {applicationDetails?.job?.user?.lastName?.charAt(0)}
            </div>
          )}
          <span className="bg-white text-primary font-medium text-xs py-1.5 px-8 rounded h-max">
            {applicationDetails?.job?.refNo ?? "-"}
          </span>
        </div>
        <div className="text-primary space-y-2 text-xs">
          <p className="font-bold">
            {" "}
            {applicationDetails?.job?.user?.firstName +
              " " +
              applicationDetails?.job?.user?.lastName ?? "-"}
          </p>
        </div>
        <div className="flex flex-col gap-3.5">
          <span className="font-medium text-primary text-xs flex-1">
            {applicationDetails?.job?.title ?? "-"}
          </span>
          <div className="flex flex-col gap-3.5 mb-6">
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">Location:</span>
              <span className="font-medium text-primary text-xs flex-1">
                {applicationDetails?.job?.location ?? "-"}
              </span>
            </div>
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">
                Industry Type:
              </span>
              <span className="font-medium text-primary text-xs flex-1">
                {applicationDetails?.job?.industryType ?? "-"}
              </span>
            </div>
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">Job Type:</span>
              <span className="font-medium text-primary text-xs flex-1">
                {
                  JobType.find(
                    (x) => x.value === applicationDetails?.job?.jobType,
                  )?.label
                }
              </span>
            </div>
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">
                Salary Range:
              </span>
              <span className="font-medium text-primary text-xs flex-1">
                £{applicationDetails?.job?.salary?.min} - £
                {applicationDetails?.job?.salary?.max}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackerTab;
